import {
  DEFAULT_TIMEOUT_MS,
  raiseIntent,
  type IntentAppId,
} from "./factsetIntentTransport";
import {
  buildPreviewChartPayload,
  type PreviewChartPayloadOptions,
} from "./chartCreatorPayload";
import { assertActiveGraphXml } from "./activeGraphXml";
import type {
  FactsetChartParameters,
  FactsetChartPreview,
  FactsetGetActiveGraphXmlResult,
  FactsetGetStylesetsResult,
  FactsetStyleset,
} from "./chartCreatorTypes";

export interface AddActiveGraphCoords {
  top?: number;
  left?: number;
  width?: number;
  height?: number;
}

export const EXCEL_ACTIVE_GRAPH_DEFAULT_COORDS: Required<AddActiveGraphCoords> = {
  top: 100,
  left: 5,
  width: 320,
  height: 200,
};

export const POWERPOINT_ACTIVE_GRAPH_DEFAULT_COORDS: Required<AddActiveGraphCoords> = {
  top: 120,
  left: 60,
  width: 480,
  height: 280,
};

const OI_REMOTE_IFRAME_APP_ID: IntentAppId = { appId: "OI Remote Iframe" };

export function buildAddActiveGraphContent(
  fdsChartXml: string,
  coords: AddActiveGraphCoords = {},
  defaults: Required<AddActiveGraphCoords> = EXCEL_ACTIVE_GRAPH_DEFAULT_COORDS,
): string {
  const xml = fdsChartXml.trim();
  assertActiveGraphXml(xml, "AddActiveGraph chart XML");
  const resolved = { ...defaults, ...coords };
  for (const [field, value] of Object.entries(resolved)) {
    if (!Number.isFinite(value)) {
      throw new Error(`ActiveGraph coordinate ${field} must be finite.`);
    }
  }
  if (resolved.left < 0 || resolved.top < 0) {
    throw new Error("ActiveGraph coordinates left and top must be non-negative.");
  }
  if (resolved.width <= 0 || resolved.height <= 0) {
    throw new Error("ActiveGraph coordinates width and height must be positive.");
  }
  return (
    `COORD_TOP=${resolved.top},` +
    `COORD_LEFT=${resolved.left},` +
    `COORD_WIDTH=${resolved.width},` +
    `COORD_HEIGHT=${resolved.height},` +
    `CHART_XML=${xml.replace(/,/g, "&cmma")}`
  );
}

function walkObjects(
  value: unknown,
  predicate: (candidate: Record<string, unknown>) => boolean,
  depth = 0,
): Record<string, unknown> | undefined {
  if (depth > 7 || value == null) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.startsWith("{")) return undefined;
    try {
      return walkObjects(JSON.parse(trimmed), predicate, depth + 1);
    } catch {
      return undefined;
    }
  }
  if (typeof value !== "object" || Array.isArray(value)) return undefined;
  const object = value as Record<string, unknown>;
  if (predicate(object)) return object;
  for (const child of Object.values(object)) {
    const found = walkObjects(child, predicate, depth + 1);
    if (found) return found;
  }
  return undefined;
}

function normalizePngData(value: unknown): string | undefined {
  if (typeof value !== "string" || !value) return undefined;
  return value.startsWith("data:") ? value : `data:image/png;base64,${value}`;
}

export async function previewChartViaIntents(
  parameters: FactsetChartParameters,
  options: PreviewChartPayloadOptions & { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<FactsetChartPreview> {
  const payload = buildPreviewChartPayload(parameters, options);
  const context = await raiseIntent(
    "factset.PreviewChart",
    {
      type: "factset.previewChart",
      body: JSON.stringify(payload),
    },
    options.timeoutMs ?? DEFAULT_TIMEOUT_MS * 4,
    OI_REMOTE_IFRAME_APP_ID,
    options.signal,
  );
  const inner =
    walkObjects(
      context,
      (candidate) =>
        "fdsChartXml" in candidate ||
        "pngData" in candidate ||
        "annotationsJson" in candidate ||
        "error" in candidate,
    ) ?? {};
  const fdsChartXml =
    typeof inner.fdsChartXml === "string" ? inner.fdsChartXml : undefined;
  return {
    fdsChartXml,
    pngDataUri:
      typeof inner.pngDataUri === "string"
        ? inner.pngDataUri
        : normalizePngData(inner.pngData),
    annotationsJson:
      typeof inner.annotationsJson === "string" ? inner.annotationsJson : undefined,
    wasStylesetChanged:
      typeof inner.wasStylesetChanged === "boolean"
        ? inner.wasStylesetChanged
        : undefined,
    error:
      inner.error === null
        ? null
        : typeof inner.error === "string"
          ? inner.error
          : !fdsChartXml && typeof inner.message === "string"
            ? inner.message
            : undefined,
  };
}

function coerceStylesets(value: unknown): FactsetStyleset[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const object = entry as Record<string, unknown>;
    return typeof object.id === "string" && typeof object.name === "string"
      ? [{ id: object.id, name: object.name }]
      : [];
  });
}

export async function getStylesetsViaIntents(options: {
  signal?: AbortSignal;
  timeoutMs?: number;
} = {}): Promise<FactsetGetStylesetsResult> {
  const context = await raiseIntent(
    "factset.GetStylesets",
    { type: "factset.getStylesets" },
    options.timeoutMs ?? DEFAULT_TIMEOUT_MS * 4,
    OI_REMOTE_IFRAME_APP_ID,
    options.signal,
  );
  const inner =
    walkObjects(context, (candidate) => Array.isArray(candidate.stylesets)) ?? {};
  return {
    stylesets: coerceStylesets(inner.stylesets),
    currentStylesetId:
      typeof inner.currentStylesetId === "string"
        ? inner.currentStylesetId
        : undefined,
  };
}

export async function getActiveGraphXmlViaIntents(
  options: { timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<FactsetGetActiveGraphXmlResult> {
  const context = await raiseIntent(
    "factset.Charting.GetActiveGraphXml",
    { type: "fdc3.nothing" },
    options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    OI_REMOTE_IFRAME_APP_ID,
    options.signal,
  );
  const inner =
    walkObjects(
      context,
      (candidate) => typeof candidate.fdsChartXml === "string",
    ) ?? {};
  return {
    fdsChartXml:
      typeof inner.fdsChartXml === "string" ? inner.fdsChartXml : undefined,
  };
}

export async function setActiveGraphXmlViaIntents(
  fdsChartXml: string,
  options: { refresh?: boolean; timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<string> {
  const xml = fdsChartXml.trim();
  assertActiveGraphXml(xml, "SetActiveGraphXml chart XML");
  const context = await raiseIntent(
    "factset.Charting.SetActiveGraphXml",
    {
      type: "factset.charting.setActiveGraphXml",
      options: {
        fdsChartXml: xml,
        refresh: options.refresh ?? true,
      },
    },
    options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    OI_REMOTE_IFRAME_APP_ID,
    options.signal,
  );
  const result =
    walkObjects(context, (candidate) => typeof candidate.message === "string") ?? {};
  return typeof result.message === "string"
    ? result.message
    : "ActiveGraph XML updated successfully";
}
